import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ShieldCheck,
  Lock,
  KeyRound,
  History,
  CheckCircle2,
  Loader2,
  Timer,
} from "lucide-react";
import { toast } from "sonner";
import { useSecurity } from "../context/SecurityContext";
import { fetchActivity, changePin, setAutoLock } from "../lib/api";
import { FamilyMembersSection } from "./FamilyMembersSection";
import { LinkedHouseholdsCard } from "./LinkedHouseholdsCard";
import { RecoveryEmailRow } from "./RecoveryEmailRow";
import { GoogleAccountRow } from "./GoogleAuth";
import { DigestSettingsRow } from "./DigestSettingsRow";
import { WeeklyDigestRow } from "./WeeklyDigestRow";
import { AnnualReviewRow } from "./AnnualReviewRow";
import { EraseVaultRow } from "./EraseVaultRow";
import { TwoFactorRow, StorageBackupRow, AccessRequestsRow, AdvisorAccessRow, EmailProviderRow, MobileNumberRow } from "./TrustRows";
import { IdentityRow } from "./IdentityRow";
import { PasskeysRow } from "./PasskeysRow";
import { PrivateModeRow } from "./PrivateModeRow";
import { ExtensionRow } from "./ExtensionRow";
import { CalendarFeedRow } from "./CalendarFeedRow";
import { EmailDeliveryRow } from "./EmailDeliveryRow";
import { MemberAvatar } from "./LockScreen";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const LOCK_OPTIONS = [1, 2, 5, 10, 15, 30];

export const SecuritySheet = ({ open, onClose }) => {
  const { status, unlocked, createPin, lockNow, refreshStatus, member, isOwner, adminAccess } = useSecurity();
  const [ownerName, setOwnerName] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [curPin, setCurPin] = useState("");
  const [changeNew, setChangeNew] = useState("");
  const [busy, setBusy] = useState(false);

  const { data: activity = [] } = useQuery({
    queryKey: ["activity"],
    queryFn: fetchActivity,
    enabled: open,
    refetchInterval: open ? 5000 : false,
  });

  const pinSet = status?.pin_set;

  const handleCreate = async () => {
    if (!/^\d{4,6}$/.test(newPin)) return toast.error("PIN must be 4–6 digits");
    if (newPin !== confirmPin) return toast.error("PINs don't match");
    setBusy(true);
    try {
      if (status?.setup_code_required && !setupCode.trim()) return toast.error("Enter the setup code from your deployment");
      await createPin(newPin, ownerName.trim() || "Owner", setupCode.trim());
      toast.success("PIN created — your vault is protected");
      setNewPin("");
      setConfirmPin("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not set PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleChange = async () => {
    if (!/^\d{4,6}$/.test(changeNew)) return toast.error("New PIN must be 4–6 digits");
    setBusy(true);
    try {
      await changePin(curPin, changeNew);
      toast.success("PIN updated");
      setCurPin("");
      setChangeNew("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change PIN");
    } finally {
      setBusy(false);
    }
  };

  const handleAutoLock = async (v) => {
    try {
      await setAutoLock(Number(v));
      await refreshStatus();
      toast.success(`Auto-lock set to ${v} min`);
    } catch {
      toast.error("Could not update auto-lock");
    }
  };

  // `hv:scroll` (from /vault?open=security&section=<testid>) scrolls a row into view and flashes it
  useEffect(() => {
    if (!open) return;
    const onScroll = (e) => {
      const el = document.querySelector(`[data-testid="${e.detail}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-primary", "rounded-xl", "transition-shadow");
      setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 2200);
    };
    window.addEventListener("hv:scroll", onScroll);
    return () => window.removeEventListener("hv:scroll", onScroll);
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent data-testid="security-sheet" className="w-full sm:max-w-md overflow-y-auto p-0">
        <div className="h-1.5 w-full bg-primary" />
        <div className="p-6">
          <SheetHeader className="text-left">
            <SheetTitle className="font-serif text-2xl flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-primary" /> Security
            </SheetTitle>
            <SheetDescription>
              Protect your vault with a PIN and auto-lock. All access is recorded below.
              {isOwner && adminAccess?.allowed && <> · <Link to="/admin" data-testid="security-admin-link" className="text-primary hover:underline">Open the Admin page</Link></>}
            </SheetDescription>
          </SheetHeader>

          {/* Status */}
          <div className="mt-5 rounded-xl border border-border bg-muted/40 p-4 flex items-center gap-3">
            <div
              className={`h-10 w-10 rounded-xl grid place-items-center ${
                pinSet ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
              }`}
            >
              {pinSet ? <ShieldCheck className="h-5 w-5" /> : <Lock className="h-5 w-5" />}
            </div>
            <div className="min-w-0">
              <div className="font-medium text-sm">
                {pinSet ? "PIN protection is on" : "No PIN set yet"}
              </div>
              <div className="text-xs text-muted-foreground">
                {pinSet ? `Auto-locks after ${status.auto_lock_minutes} min idle` : "Your vault is currently open"}
              </div>
              {pinSet && member && (
                <div data-testid="signed-in-as" className="mt-1.5 inline-flex items-center gap-1.5 text-xs">
                  <MemberAvatar member={member} size="h-5 w-5 text-[9px]" />
                  Signed in as <span className="font-medium">{member.name}</span>
                  <span className="uppercase tracking-wider font-mono text-[10px] text-muted-foreground">· {member.role_label}</span>
                </div>
              )}
            </div>
            {pinSet && unlocked && (
              <Button
                data-testid="lock-now-btn"
                variant="outline"
                size="sm"
                onClick={lockNow}
                className="ml-auto gap-1.5"
              >
                <Lock className="h-3.5 w-3.5" /> Lock
              </Button>
            )}
          </div>

          {/* Create PIN */}
          {!pinSet && (
            <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4 space-y-3">
              <h4 className="text-xs uppercase tracking-wider font-mono text-primary flex items-center gap-1.5">
                <KeyRound className="h-3.5 w-3.5" /> Create your owner PIN
              </h4>
              <Input
                data-testid="owner-name-input"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                placeholder="Your name (shown on the lock screen)"
                className="bg-card"
              />
              {status?.setup_code_required && <Input data-testid="setup-code-input" type="password" autoComplete="off" value={setupCode} onChange={(e) => setSetupCode(e.target.value)} placeholder="Setup code (SETUP_CODE from your deployment)" className="bg-card font-mono" />}
              <Input
                data-testid="new-pin-input"
                inputMode="numeric"
                maxLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                placeholder="New PIN (4–6 digits)"
                className="bg-card"
              />
              <Input
                data-testid="confirm-pin-input"
                inputMode="numeric"
                maxLength={6}
                value={confirmPin}
                onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
                placeholder="Confirm PIN"
                className="bg-card"
              />
              <Button
                data-testid="create-pin-btn"
                onClick={handleCreate}
                disabled={busy}
                className="w-full gap-2 bg-primary hover:bg-primary/90"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Protect my vault
              </Button>
            </div>
          )}

          {/* Auto-lock + Change PIN */}
          {pinSet && (
            <>
              <div className="mt-5" data-testid="auto-lock-section">
                <label className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5">
                  <Timer className="h-3.5 w-3.5" /> Auto-lock after
                </label>
                <Select value={String(status.auto_lock_minutes)} onValueChange={handleAutoLock} disabled={!isOwner}>
                  <SelectTrigger data-testid="auto-lock-select" className="mt-1.5 bg-card">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LOCK_OPTIONS.map((m) => (
                      <SelectItem key={m} value={String(m)}>
                        {m} minute{m > 1 ? "s" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="mt-5 rounded-xl border border-border p-4 space-y-3">
                <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground flex items-center gap-1.5">
                  <KeyRound className="h-3.5 w-3.5" /> Change PIN
                </h4>
                <Input
                  data-testid="current-pin-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={curPin}
                  onChange={(e) => setCurPin(e.target.value.replace(/\D/g, ""))}
                  placeholder="Current PIN"
                  className="bg-card"
                />
                <Input
                  data-testid="change-new-pin-input"
                  inputMode="numeric"
                  maxLength={6}
                  value={changeNew}
                  onChange={(e) => setChangeNew(e.target.value.replace(/\D/g, ""))}
                  placeholder="New PIN"
                  className="bg-card"
                />
                <Button
                  data-testid="change-pin-btn"
                  onClick={handleChange}
                  disabled={busy}
                  variant="outline"
                  className="w-full gap-2"
                >
                  Update PIN
                </Button>
              </div>
            </>
          )}

          {pinSet && isOwner && <RecoveryEmailRow hint={status.recovery_email_hint} onSaved={refreshStatus} />}
          {pinSet && isOwner && <GoogleAccountRow linkedHint={status.google_linked_hint} onChanged={refreshStatus} />}
          {pinSet && isOwner && <AccessRequestsRow />}
          {pinSet && <IdentityRow />}
          {pinSet && <MobileNumberRow />}
          {pinSet && isOwner && <AdvisorAccessRow />}
          {pinSet && isOwner && <EmailProviderRow />}
          {pinSet && isOwner && <TwoFactorRow />}
          {pinSet && isOwner && <PasskeysRow />}
          {pinSet && member?.role !== "executor" && <ExtensionRow />}
          {pinSet && <CalendarFeedRow />}
          {pinSet && isOwner && <StorageBackupRow />}
          {pinSet && <PrivateModeRow isOwner={isOwner} />}
          {pinSet && isOwner && <DigestSettingsRow />}
          {pinSet && isOwner && <WeeklyDigestRow />}
          {pinSet && isOwner && <AnnualReviewRow />}
          {pinSet && isOwner && <EmailDeliveryRow />}
          {pinSet && <FamilyMembersSection />}
          {pinSet && <LinkedHouseholdsCard />}

          {/* Activity log */}
          <div className="mt-6">
            <h4 className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3 flex items-center gap-1.5">
              <History className="h-3.5 w-3.5" /> Recent activity
              <Link to="/audit" className="ml-auto normal-case tracking-normal font-sans text-primary hover:underline" data-testid="security-audit-link">Full audit trail & reports →</Link>
            </h4>
            <div data-testid="activity-log" className="space-y-2">
              {activity.length === 0 && (
                <p className="text-sm text-muted-foreground py-2">No activity recorded yet.</p>
              )}
              {activity.map((a) => (
                <div
                  key={a.id}
                  data-testid="activity-item"
                  className="flex items-start gap-2.5 rounded-lg border border-border bg-card px-3 py-2"
                >
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">{a.label}</div>
                    {(a.detail || a.by) && (
                      <div className="text-xs text-muted-foreground truncate">
                        {a.by && <span className="font-medium text-foreground/80">{a.by}</span>}
                        {a.by && a.detail && " · "}
                        {a.detail}
                      </div>
                    )}
                  </div>
                  <div className="text-[11px] font-mono text-muted-foreground whitespace-nowrap">
                    {new Date(a.at).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {pinSet && isOwner && <EraseVaultRow />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
